import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import {
  GET_ROADS,
  GET_ROADS_BY_IDS,
  GET_ROAD_BY_ID,
} from '../gql/queries/geoPositions/roads';
import { type DirectionLabels } from '../components/inputs/buttons/ArrowDirectionButton/types';
import { type RoadPkRange } from '../utils/pkValidation';

/**
 * Extremos (PK inicio/fin) de la carretera del recorrido, para validar que el
 * PK de un tramo no se sale de ella (2026-08-10).
 *
 * 🔴 **`GET_ROAD_BY_ID` con `cache-first`, y NO el `readRoads` @client
 * `cache-only` que usa su hermano de las etiquetas.** Las carreteras las
 * precarga el LISTADO de recorridos (`GeneralViewRoute/loader.ts`) y el
 * asistente, pero **no el detalle**: quien entra directo al detalle —recargar
 * con F5, un enlace, o la tablet abriendo ya dentro de una ruta— se encuentra
 * la caché fría, y con `cache-only` la validación se quedaba en nada, en
 * silencio. Con `cache-first` no cuesta red en el caso normal (la carretera ya
 * está) y la trae cuando no.
 *
 * Sigue funcionando OFFLINE: sin red y con caché fría la query falla, se
 * devuelve `undefined`, los campos `pkInitRoad`/`pkEndRoad` del formulario
 * quedan vacíos y el `.test()` del schema no valida rango — un Tablet sin
 * cobertura tiene que poder abrir y cerrar tramos. El backend valida igual.
 */
export const useRoadPkRange = (roadId?: string): RoadPkRange | undefined => {
  const { data } = useQuery(GET_ROAD_BY_ID, {
    variables: { id: roadId },
    skip: !roadId,
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  const road = data?.road;

  /**
   * 🔴 `useMemo` OBLIGATORIO, no es una optimización.
   *
   * Devolver un objeto literal nuevo en cada render hacía que los `useEffect`
   * que dependen de este valor —los cuatro modales lo usan para sembrar
   * `pkInitRoad`/`pkEndRoad` en el formulario— se dispararan en BUCLE:
   * efecto → `setValue` → render → objeto nuevo → efecto… Los tests no
   * fallaban con un mensaje claro, se quedaban colgados 2,5 minutos hasta
   * agotar el timeout.
   */
  return useMemo(() => {
    if (
      !road ||
      road.pkInitKm == null ||
      road.pkInitMeter == null ||
      road.pkEndKm == null ||
      road.pkEndMeter == null
    ) {
      return undefined;
    }
    return {
      pkInitKm: road.pkInitKm,
      pkInitMeter: road.pkInitMeter,
      pkEndKm: road.pkEndKm,
      pkEndMeter: road.pkEndMeter,
    };
  }, [road?.pkInitKm, road?.pkInitMeter, road?.pkEndKm, road?.pkEndMeter]);
};

/**
 * Etiquetas de sentido (extremos de la carretera, p.ej. `Lleida`/`Girona`)
 * para pintar bajo las flechas ↓/↑ del `ArrowDirectionButton` — mismo dato y
 * mapeo que `NewWorkOrderForm`/`ShowOT`: `road.directions[0]` bajo `↓` (down)
 * y `road.directions[último]` bajo `↑` (top).
 *
 * Lee de la CACHÉ Apollo (`GET_ROADS_BY_IDS` es `@client` → resolver local
 * `readRoads`) con `cache-only`, sin red: las carreteras son datos maestros
 * precargados en el arranque, así que funciona offline (Tablet). Si la
 * carretera no está en caché devuelve `{}` y el botón simplemente no muestra
 * etiquetas — degradación suave, igual que el formulario de creación cuando
 * aún no hay `road` seleccionada.
 */
export const useRoadDirectionLabels = (roadId?: string): DirectionLabels => {
  const { data } = useQuery(GET_ROADS_BY_IDS, {
    variables: { ids: roadId ? [roadId] : [] },
    skip: !roadId,
    fetchPolicy: 'cache-only',
  });

  const directions: string[] = data?.readRoads?.[0]?.directions ?? [];
  if (directions.length === 0) return {};

  return {
    down: directions[0],
    top: directions[directions.length - 1],
  };
};

/**
 * Nombres de varias carreteras por id (multi-carretera 2026-08-15).
 *
 * Mismo mecanismo que `useRoadDirectionLabels`: lee de la CACHÉ Apollo con
 * `cache-only`, sin red — las carreteras son datos maestros precargados en el
 * arranque, así que funciona offline en la tablet. Lo que no esté en caché
 * simplemente no aparece, y quien lo consume pinta un guion.
 */
/**
 * Las carreteras del recorrido, resueltas por id.
 *
 * 🔴 **`GET_ROADS` con `cache-first`, y NO el `readRoads` @client `cache-only`
 * que usan las etiquetas de sentido** — por el mismo motivo que
 * `useRoadPkRange`, que ya lo documenta arriba: **el DETALLE del recorrido no
 * precarga las carreteras**. Quien entra directo —recargar con F5, un enlace, o
 * la tablet abriendo ya dentro de una ruta— se encuentra la caché fría, y con
 * `cache-only` esto devolvía el mapa vacío EN SILENCIO: la columna «Carretera»
 * sin rellenar, el mapa sin los extremos con los que acotar cada vía, y el
 * selector de carretera del modal de tramo listando ObjectIds en crudo.
 *
 * `GET_ROADS` es la query de datos maestros (una sola, todas las carreteras del
 * tenant): en el caso normal ya está en caché y no cuesta red, y cuando no está
 * la trae. Sin cobertura y en frío devuelve vacío, que es lo mismo que antes.
 */
const useRoadsByIds = (roadIds?: string[] | null) => {
  const ids = roadIds ?? [];
  const { data } = useQuery(GET_ROADS, {
    skip: ids.length === 0,
    fetchPolicy: 'cache-first',
  });

  return useMemo(() => {
    const wanted = new Set(ids);
    return (data?.roads ?? []).filter((road: { id?: string }) =>
      road?.id ? wanted.has(road.id) : false
    );
    // `ids.join` y no `ids`: el array llega nuevo en cada render.
  }, [data?.roads, ids.join(',')]);
};

/**
 * Extremos declarados de varias carreteras, por id.
 *
 * Lo consume el mapa del detalle para dibujar el trozo de una carretera que el
 * recorrido no acota explícitamente: sin ellos habría que filtrarla por el
 * rango de la primera, y los kilometrajes de una troncal y su ramal no son
 * comparables — saldría un trozo arbitrario.
 */
export const useRoadRangesById = (
  roadIds?: string[] | null
): Record<string, RoadPkRange> => {
  const roads = useRoadsByIds(roadIds);

  return useMemo(() => {
    const out: Record<string, RoadPkRange> = {};
    for (const road of roads) {
      if (
        road?.id &&
        road.pkInitKm != null &&
        road.pkInitMeter != null &&
        road.pkEndKm != null &&
        road.pkEndMeter != null
      ) {
        out[road.id] = {
          pkInitKm: road.pkInitKm,
          pkInitMeter: road.pkInitMeter,
          pkEndKm: road.pkEndKm,
          pkEndMeter: road.pkEndMeter,
        };
      }
    }
    return out;
  }, [roads]);
};

/** Nombre de cada carretera del recorrido, por id. */
export const useRoadNamesById = (
  roadIds?: string[] | null
): Record<string, string> => {
  const ids = roadIds ?? [];
  const roads = useRoadsByIds(roadIds);

  return useMemo(() => {
    const out: Record<string, string> = {};
    for (const road of roads) {
      if (road?.id && road?.name) out[road.id] = road.name;
    }
    // Una carretera que no resuelva tiene que seguir contando para decidir si
    // la columna se pinta: si no, un recorrido de dos saldría como de una.
    for (const id of ids) if (!(id in out)) out[id] = '';
    return out;
  }, [roads, ids.join(',')]);
};
